/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * ECharts canvas/SVG renderer'ı CSS `var(...)` ifadelerini tarayıcı CSS motoru
 * gibi çözemez. Bu yardımcı, chart option içindeki token referanslarını render
 * öncesinde gerçek renklere dönüştürür.
 */

export type ChartCssTokenReader = (tokenName: string) => string;

const CSS_VAR_PATTERN = /^var\(\s*(--[\w-]+)(?:\s*,\s*(.+))?\s*\)$/;

function resolveCssVarString(value: string, readToken: ChartCssTokenReader): string {
  let current = value.trim();

  for (let depth = 0; depth < 8; depth += 1) {
    const match = CSS_VAR_PATTERN.exec(current);
    if (!match) return current;

    const [, tokenName, fallback] = match;
    const resolved = readToken(tokenName).trim();
    const next = resolved || fallback?.trim();
    if (!next || next === current) return current;
    current = next;
  }

  return current;
}

/**
 * Chart option nesnesini değiştirmeden CSS token'larını çözer.
 * Fonksiyonlar ve class instance'ları aynen korunur.
 */
export function resolveChartCssVariables<T>(value: T, readToken: ChartCssTokenReader): T {
  const tokenCache = new Map<string, string>();
  const seen = new WeakMap<object, unknown>();
  const cachedReader = (tokenName: string) => {
    if (!tokenCache.has(tokenName)) {
      tokenCache.set(tokenName, readToken(tokenName));
    }
    return tokenCache.get(tokenName) ?? '';
  };

  const visit = (input: unknown): unknown => {
    if (typeof input === 'string') {
      return resolveCssVarString(input, cachedReader);
    }
    if (input == null || typeof input !== 'object') {
      return input;
    }
    if (seen.has(input)) {
      return seen.get(input);
    }
    if (Array.isArray(input)) {
      const result: unknown[] = [];
      seen.set(input, result);
      input.forEach((item) => result.push(visit(item)));
      return result;
    }

    const prototype = Object.getPrototypeOf(input);
    if (prototype !== Object.prototype && prototype !== null) {
      return input;
    }

    const result: Record<string, unknown> = {};
    seen.set(input, result);
    Object.entries(input).forEach(([key, item]) => {
      result[key] = visit(item);
    });
    return result;
  };

  return visit(value) as T;
}
